import { useCallback, useEffect, useState } from 'react';
import { env } from '../config/env';
import type { AppConfig } from '../config';
import { kioskShell } from '../config/kioskShell';
import { retryArWarmNow, useArWarmStatus } from '../ar';
import { useDiagnostics } from '../lib/diagnostics';
import { liveObjectUrlCount } from '../lib/objectUrls';
import { pickMimeType, smsFriendly } from '../capture';
import { queueSnapshot, refreshDepth, retryNow } from '../upload';
import { maskPhone } from '../lib/phone';
import type { QueueRecord } from '../upload';

/**
 * Show-night diagnostics. Reached with ?admin=1 or ctrl+shift+A.
 *
 * Built early and deliberately ugly. The question this has to answer in a dark
 * theater lobby is "is it sending, and if not, why" — so queue depth, last
 * error, session count, and the device IDs are all above the fold with no
 * interaction needed.
 */
export function AdminScreen({ config, onClose }: { config: AppConfig; onClose: () => void }) {
  const d = useDiagnostics();
  // Present only inside the native Android shell; null in any browser.
  const shell = kioskShell();
  const warm = useArWarmStatus();
  const [records, setRecords] = useState<Omit<QueueRecord, 'blob'>[]>([]);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    void refreshDepth();
    void queueSnapshot().then(setRecords);
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    refresh();
    const t = window.setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, [refresh]);

  const mime = pickMimeType();

  return (
    <div className="h-full min-h-[var(--app-h)] w-full overflow-y-auto bg-black p-4 font-mono text-xs text-green-400">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-base font-bold uppercase text-green-300">Incomer diagnostics</h1>
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-green-700 px-4 py-2 text-green-300"
        >
          close
        </button>
      </div>

      <Section title="Delivery">
        <Row label="queue depth" value={String(d.queueDepth)} alert={d.queueDepth > 5} />
        <Row label="awaiting sms" value={String(d.smsQueued)} />
        <Row label="uploads confirmed" value={String(d.uploadsConfirmed)} />
        <Row label="draining now" value={d.queueSending ? 'yes' : 'no'} />
        <Row label="online" value={navigator.onLine ? 'yes' : 'NO'} alert={!navigator.onLine} />
        <Row
          label="supabase"
          value={env.supabaseUrl ? env.supabaseUrl.replace(/^https?:\/\//, '') : 'NOT CONFIGURED'}
          alert={!env.supabaseUrl}
        />
      </Section>

      <Section title="Last error">
        {d.lastError ? (
          <div className="text-red-400" data-selectable>
            <div>{new Date(d.lastError.at).toLocaleTimeString()} · {d.lastError.where}</div>
            <div className="break-words">{d.lastError.message}</div>
          </div>
        ) : (
          <div className="text-green-700">none</div>
        )}
      </Section>

      <Section title="Sessions">
        <Row label="started" value={String(d.sessionCount)} />
        <Row label="completed" value={String(d.completedCount)} />
        <Row label="retakes visible" value="see rows below" />
      </Section>

      <Section title="Devices">
        <Row label="mode" value={config.mode} />
        <Row label="ar module" value={d.arSource} alert={d.arSource === 'stub'} />
        <Row label="camera id" value={d.cameraDeviceId ?? config.cameraDeviceId ?? '(default)'} />
        <Row label="camera" value={d.cameraLabel ?? '—'} />
        <Row label="mic id" value={d.micDeviceId ?? config.micDeviceId ?? '(default)'} />
        <Row label="mic" value={d.micLabel ?? '—'} />
        <Row
          label="mic granted"
          value={d.micGranted === null ? 'not asked' : d.micGranted ? 'yes' : 'no'}
          alert={d.micGranted === false}
        />
      </Section>

      {/*
        Which camera the panel is about to open, and why every other one was
        rejected. This is the section to read first on the unit: the wrong
        device here is the RK628 HDMI bridge, and opening it kernel-panics the
        board. See src/capture/selectKioskCamera.ts.
      */}
      {shell ? (
        <Section title="Platform">
          <Row label="shell" value={`android ${shell.shellVersion}`} />
          <Row label="webview" value={shell.webViewVersion} />
          <Row
            label="viewport"
            value={`${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}`}
          />
        </Section>
      ) : null}

      {shell ? (
        <Section title={`Cameras (${shell.cameraCount})`}>
          {shell.cameras.length === 0 ? (
            <Row label="none" value="the shell enumerated no cameras" alert />
          ) : (
            shell.cameras.map((c) => (
              <Row
                key={c.index}
                label={`[${c.index}] ${c.facing}/${c.level}`}
                value={
                  c.index === shell.safeCameraIndex
                    ? `CHOSEN — ${d.cameraLabel ?? 'label hidden until grant'}`
                    : c.safe
                      ? 'safe, not chosen'
                      : 'REJECTED — HDMI bridge'
                }
                alert={!c.safe}
              />
            ))
          )}
          {shell.safeCameraIndex < 0 ? (
            <Row label="resolved" value="NONE — the flow will refuse to open a camera" alert />
          ) : null}
        </Section>
      ) : null}

      {/*
        Whether the AR engine is actually up. This is the one thing on the unit
        that cannot recover by itself if it is wrong at showtime: Camera Kit
        bootstraps from Snap's CDN on every page load, so a kiosk that booted
        before the venue network did needs either the network or this button.
      */}
      {env.arModule === 'camera-kit' ? (
        <Section title="AR engine">
          <Row
            label="state"
            value={warm.state}
            alert={warm.state === 'failed' || warm.state === 'idle'}
          />
          <Row label="attempts" value={String(warm.attempts)} />
          <Row label="warm took" value={warm.durationMs ? `${warm.durationMs}ms` : '—'} />
          <Row label="engine" value={env.ckWasmEndpoint ? 'self-hosted' : 'snap cdn'} />
          {warm.lastError ? (
            <div className="text-red-400" data-selectable>
              {warm.lastError}
            </div>
          ) : null}
          {warm.state !== 'ready' ? (
            <button
              type="button"
              onClick={() => retryArWarmNow()}
              className="mt-2 rounded border border-green-700 px-3 py-2 text-green-300"
            >
              warm now
            </button>
          ) : null}
        </Section>
      ) : null}

      <Section title="Recorder">
        <Row label="mime" value={mime ?? 'UNSUPPORTED'} alert={!mime} />
        <Row label="in use" value={d.recorderMimeType ?? '—'} />
        <Row
          label="sms friendly"
          value={smsFriendly(mime) ? 'yes (mp4)' : 'NO — needs transcode'}
          alert={!smsFriendly(mime)}
        />
      </Section>

      <Section title="Memory">
        <Row
          label="live object urls"
          value={String(liveObjectUrlCount())}
          alert={liveObjectUrlCount() > 3}
        />
        <Row label="refreshed" value={`${tick} ticks`} />
      </Section>

      {config.queuedUpload ? (
        <Section title={`Queue rows (${records.length})`}>
          <button
            type="button"
            onClick={() => void retryNow()}
            className="mb-2 rounded border border-green-700 px-3 py-2 text-green-300"
          >
            retry all now
          </button>
          {records.length === 0 ? (
            <div className="text-green-700">empty</div>
          ) : (
            <table className="w-full text-left">
              <thead className="text-green-600">
                <tr>
                  <th>id</th>
                  <th>status</th>
                  <th>att</th>
                  <th>phone</th>
                  <th>error</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id} className={r.status === 'failed' ? 'text-red-400' : ''}>
                    <td className="pr-2">{r.id.slice(0, 8)}</td>
                    <td className="pr-2">{r.status}</td>
                    <td className="pr-2">{r.attempts}</td>
                    <td className="pr-2">{r.phone ? maskPhone(r.phone) : '—'}</td>
                    <td className="break-all">{r.lastError ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      ) : null}

      <Section title="Event log">
        <div className="max-h-64 overflow-y-auto" data-selectable>
          {d.events
            .slice()
            .reverse()
            .map((e, i) => (
              <div key={`${e.at}-${i}`} className="text-green-600">
                {new Date(e.at).toLocaleTimeString()} {e.message}
              </div>
            ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 border border-green-900 p-3">
      <h2 className="mb-2 uppercase text-green-300">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, value, alert = false }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-green-700">{label}</span>
      <span className={`break-all text-right ${alert ? 'text-red-400' : ''}`} data-selectable>
        {value}
      </span>
    </div>
  );
}
